import React, { useEffect, useState, useMemo } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import MetricCard from "../../performance/MetricCard";
import {
  PerformanceContainer,
  MetricsGrid,
  KPIProductsGrid,
} from "../../performance/PerformanceStyles";
import {
  fetchProductMetrics,
  fetchKPIProducts,
  fetchDateYearMetrics,
} from "store/performance-report/PerformanceReportActions";
import { MetricCategory } from "store/performance-report/PerformanceReportTypes";
import {
  useSetGoalHandler,
  useDeleteGoalHandler,
} from "hooks/useSetGoalHandler";
import {
  selectPerformanceReportStoreId,
  selectPerformanceReportDateRange,
  selectIsReportAPIAvailable,
  selectProductMetrics,
  selectKPIViewProducts,
  selectIsKPIProductsLoading,
  selectDateYearMetrics,
  selectIsProductMetricsAlreadyFetched,
} from "store/performance-report/PerformanceReportSelectors";
import {
  selectKpiCategoryById,
  selectKpiConfigIsLoaded,
} from "store/kpi-config/KpiConfigSelectors";
import {
  getBasicMetricsConfig,
  getProductListOptions,
  getKPIMetricConfigFromDynamic,
  convertBasicMetricsToPayload,
  getAllMetricIds,
  getMetricsByDisplayType,
  findMetricById,
  getMetricGoal,
  isMetricGoalEnabled,
} from "utils/KpiConfigUtils";
import {
  calculateDateRanges,
  transformMetricsData,
  getDaysFromPeriodType,
  calculateDimensionId,
  calculateGranularity,
  createMetricLineCharts,
} from "utils/PerformanceReportUtils";

import { CircularProgress, FormControl, Typography } from "@mui/material";
import DropdownMenu from "components/common/DropdownMenu";
import {
  ProductsGrid,
  styles,
  StyledDiv,
} from "components/performance/PerformanceStyles";
import ProductItemComponent from "components/performance/ProductItemComponent";
import { selectAllProductsFetched } from "store/product/ProductSelectors";
import { selectAdapterInfo } from "store/adapter-config/AdapterConfigSelectors";
import { AdapterInfoObject } from "utils/AppConfigUtils";
import { createCategoriesDisplayedHandler } from "utils/ProductUtil";

const ProductComponent = () => {
  const intl = useIntl();
  const { bodyWrap } = styles();
  const dispatch = useDispatch();
  const isKpiConfigLoaded = useSelector(selectKpiConfigIsLoaded);
  const kpiProducts = useSelector(selectKPIViewProducts);
  const products = useSelector(selectAllProductsFetched);
  const isKPIProductsLoading = useSelector(selectIsKPIProductsLoading);
  const [categoriesDisplayedProductIds, setCategoriesDisplayedProductIds] =
    useState<Set<string>>(new Set());
  const performanceStoreId = useSelector(selectPerformanceReportStoreId);
  const performanceDateRange = useSelector(selectPerformanceReportDateRange);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  const productMetricsData = useSelector(selectProductMetrics);
  const productCategory = useSelector((state: any) =>
    selectKpiCategoryById("product")(state),
  );
  const isAlreadyFetched = useSelector(selectIsProductMetricsAlreadyFetched);

  const productMetricsConfig =
    isKpiConfigLoaded && productCategory
      ? getBasicMetricsConfig(productCategory)
      : [];
  const productMetrics = transformMetricsData(
    productMetricsConfig,
    productMetricsData,
    intl,
  );
  const dateYearMetricsData = useSelector(
    selectDateYearMetrics(MetricCategory.PRODUCT),
  );
  const dateRangeDays = getDaysFromPeriodType(performanceDateRange);
  const dimensionId = calculateDimensionId(dateRangeDays);
  const granularity = calculateGranularity(dateRangeDays);
  const adapterInfo: AdapterInfoObject = useSelector(selectAdapterInfo);
  const isProductIdAnalyticIdAPIAvailable =
    adapterInfo?.operations?.includes(
      "POST /v1/{accountId}/product-analytic-id-query",
    ) ?? false;
  const metricLineCharts = createMetricLineCharts(
    dateYearMetricsData,
    productMetricsConfig,
    granularity,
    intl,
    "product",
  );
  const kpiProductOptions = useMemo(() => {
    return isKpiConfigLoaded && productCategory
      ? getProductListOptions(productCategory)
      : [];
  }, [isKpiConfigLoaded, productCategory]);
  const defaultSelectedView =
    kpiProductOptions.length > 0 ? kpiProductOptions[0].value : "";
  const [selectedView, setSelectedView] = useState("");
  const effectiveSelectedView = selectedView || defaultSelectedView;
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    if (isAlreadyFetched) {
      return;
    }

    if (
      performanceStoreId &&
      performanceDateRange &&
      isReportAPIAvailable &&
      isKpiConfigLoaded &&
      productCategory
    ) {
      const { currentPeriod, comparisonPeriod } =
        calculateDateRanges(performanceDateRange);
      setDateRange({
        startDate: currentPeriod.startDate,
        endDate: currentPeriod.endDate,
      });
      const basicMetrics = getMetricsByDisplayType(productCategory, "basic");
      const metricsPayload = convertBasicMetricsToPayload(basicMetrics);

      dispatch(
        fetchProductMetrics(
          performanceStoreId,
          currentPeriod,
          comparisonPeriod,
          metricsPayload,
        ),
      );

      const allMetricIds = getAllMetricIds(basicMetrics);
      dispatch(
        fetchDateYearMetrics({
          storeId: performanceStoreId,
          dateRange: currentPeriod,
          dimension: { id: dimensionId },
          metrics: allMetricIds,
          category: MetricCategory.PRODUCT,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    performanceStoreId,
    performanceDateRange,
    isReportAPIAvailable,
    isKpiConfigLoaded,
    productCategory,
    dateRangeDays,
    isAlreadyFetched,
  ]);

  useEffect(() => {
    if (
      performanceStoreId &&
      performanceDateRange &&
      isReportAPIAvailable &&
      isKpiConfigLoaded &&
      productCategory &&
      effectiveSelectedView
    ) {
      const { currentPeriod } = calculateDateRanges(performanceDateRange);
      const selectedMetric =
        findMetricById(productCategory, effectiveSelectedView) ||
        getMetricsByDisplayType(productCategory, "productList")[0];

      if (selectedMetric) {
        const {
          metrics: currentMetrics,
          sortMetric,
          sortDescending,
          sortBy: sortByArray,
        } = getKPIMetricConfigFromDynamic(
          productCategory,
          effectiveSelectedView,
        );

        dispatch(
          fetchKPIProducts({
            storeId: performanceStoreId,
            dateRange: currentPeriod,
            limit: selectedMetric.api.limit || 24,
            selectedView: effectiveSelectedView,
            dimension: { id: "productId" },
            metrics: currentMetrics.map((m: string) => ({ id: m })),
            sortBy: sortByArray || {
              metricId: sortMetric,
              desc: sortDescending,
            },
            isProductIdAnalyticIdAPIAvailable,
          }),
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    performanceStoreId,
    performanceDateRange,
    isReportAPIAvailable,
    isKpiConfigLoaded,
    productCategory,
    effectiveSelectedView,
  ]);

  const handleViewChange = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const actualValue = event.target.value as string;
    setSelectedView(actualValue);
  };

  const handleCategoriesDisplayedChange = createCategoriesDisplayedHandler(
    setCategoriesDisplayedProductIds,
  );

  const handleSetGoal = useSetGoalHandler("product");
  const handleDeleteGoal = useDeleteGoalHandler("product");

  return (
    <>
      {performanceStoreId && performanceDateRange && (
        <PerformanceContainer>
          <MetricsGrid>
            {productMetrics.map((metric) => {
              const goalEnabled = isMetricGoalEnabled(
                productCategory,
                metric.id,
              );
              return (
                <MetricCard
                  key={metric.id}
                  data={metric}
                  lineChartData={metricLineCharts[metric.id]}
                  onSetGoal={goalEnabled ? handleSetGoal : undefined}
                  onDeleteGoal={goalEnabled ? handleDeleteGoal : undefined}
                  goal={
                    goalEnabled
                      ? getMetricGoal(productCategory, metric.id)
                      : undefined
                  }
                />
              );
            })}
          </MetricsGrid>
          <ProductsGrid>
            <FormControl sx={bodyWrap}>
              <label htmlFor="kpi-product-menu">
                <Typography variant="subHeader">
                  {intl.formatMessage({
                    id: "productComponent.view",
                    defaultMessage: "View:",
                  })}
                </Typography>
              </label>
              <DropdownMenu
                menuId="KPIProductMenu"
                options={kpiProductOptions}
                value={effectiveSelectedView}
                changeHandler={handleViewChange}
              />
            </FormControl>

            {/* KPI Products Display */}
            {(() => {
              if (kpiProducts?.rows && kpiProducts.rows.length > 0) {
                return (
                  <KPIProductsGrid>
                    {kpiProducts.rows.map((row: any, index: number) => {
                      const productId = row.dimensionValue;
                      const product = products[productId];
                      const isProductLoaded =
                        product?.productDetailsLoaded ?? false;
                      const {
                        type: metricType,
                        isMultiMetric,
                        displayMetrics,
                        metrics,
                      } = getKPIMetricConfigFromDynamic(
                        productCategory,
                        effectiveSelectedView,
                      );

                      // Always show ProductItemComponent when we have KPI row data
                      // This allows displaying analytic ID and metrics even when
                      // product details couldn't be loaded (e.g., productId couldn't be resolved)
                      return (
                        <ProductItemComponent
                          key={productId}
                          index={index + 1}
                          product={isProductLoaded ? product : undefined}
                          code={product?.code ?? productId}
                          price={product?.highPrice}
                          productId={product?.productId ?? productId}
                          productMetricValue={row.metricValues[0]}
                          productMetricValues={
                            isMultiMetric ? row.metricValues : undefined
                          }
                          selectedView={effectiveSelectedView}
                          metricType={metricType}
                          dateRange={dateRange}
                          isCategoriesDisplayed={categoriesDisplayedProductIds.has(
                            product?.productId ?? productId,
                          )}
                          setIsCategoriesDisplayed={(value: boolean) =>
                            handleCategoriesDisplayedChange(
                              value,
                              product?.productId ?? productId,
                            )
                          }
                          displayMetrics={displayMetrics}
                          metricIds={metrics}
                        />
                      );
                    })}
                  </KPIProductsGrid>
                );
              }
              if (isKPIProductsLoading) {
                return (
                  <StyledDiv className="progress">
                    <CircularProgress size="40px" />
                  </StyledDiv>
                );
              }
              return null;
            })()}
          </ProductsGrid>
        </PerformanceContainer>
      )}
    </>
  );
};

export default ProductComponent;
